import React from "react";
import "./style.css";

const featureCards = [
    {
        title: "Meal and calorie tracking",
        body: "Calo helps users log meals, estimate calories, review macros, and keep a simple nutrition diary throughout the day.",
    },
    {
        title: "Workout and health summaries",
        body: "When users connect supported health providers, Calo can display workout time, calories burned, body weight, and related wellness data inside the app.",
    },
    {
        title: "Personalized guidance",
        body: "Calo combines nutrition, activity, and profile information to create a more personalized weight-management experience.",
    },
];

const dataCards = [
    {
        title: "Health data we may request",
        items: [
            "Exercise sessions and workout duration",
            "Calories burned and related activity summaries",
            "Body weight and other wellness records the user chooses to share",
            "Nutrition-related records needed for in-app progress views",
        ],
    },
    {
        title: "Why we request it",
        items: [
            "To show synced workouts alongside meal and calorie tracking",
            "To give users a clearer picture of intake versus activity",
            "To personalize recommendations and progress insights",
            "To support optional connections with services such as Apple Health and Fitbit",
        ],
    },
    {
        title: "User control",
        items: [
            "Users explicitly choose whether to connect a health provider",
            "Users can continue using Calo without enabling health sync",
            "Access is limited to the scopes required for enabled features",
            "Support is available at website@caloapp.ai",
        ],
    },
];

export const AppPage = () => {
    return (
        <div className="google-app-page">
            <header className="google-app-header">
                <a className="google-app-brand" href="/">
                    <span className="google-app-brand-text">Calo</span>
                </a>
                <nav className="google-app-nav">
                    <a href="/privacy-policy">Privacy Policy</a>
                    <a href="/terms-of-use">Terms of Use</a>
                    <a href="mailto:website@caloapp.ai">Support</a>
                </nav>
            </header>

            <main className="google-app-main">
                <section className="google-app-hero">
                    <div className="google-app-hero-copy">
                        <div className="google-app-hero-shell">
                            <div className="google-app-hero-main">
                                <p className="google-app-eyebrow">Official app information</p>
                                <h1>Calo: AI Food Calorie Counter</h1>
                                <p className="google-app-subtitle">
                                    Calo helps people log meals, understand calories, track macros,
                                    monitor body weight, and review activity data in one clean health
                                    workspace. It is built for everyday nutrition awareness, weight
                                    management, and optional sync with Apple Health and Fitbit.
                                </p>

                                <div className="google-app-actions">
                                    <a className="google-app-primary" href="/privacy-policy">Read Privacy Policy</a>
                                    <a className="google-app-secondary" href="mailto:website@caloapp.ai">Contact Support</a>
                                </div>
                            </div>

                            <aside className="google-app-download" aria-label="Download Calo">
                                <p className="google-app-download-label">Download Calo</p>
                                <p className="google-app-download-title">Get Calo on iPhone or Android.</p>
                                <p className="google-app-download-note">
                                    Use the same Calo account across devices and keep health sync
                                    setup in one place.
                                </p>
                                <div className="google-app-download-badges">
                                    <a
                                        className="google-app-store-link"
                                        href="https://apps.apple.com/us/app/calo-ai-food-calorie-counter/id6447434453"
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label="Download Calo on the App Store"
                                    >
                                        <img
                                            src="/static/image/homepage/app-store-en.png"
                                            alt="Download on the App Store"
                                        />
                                    </a>
                                    <a
                                        className="google-app-store-link"
                                        href="https://play.google.com/store/apps/details?id=calo.ai.calorie.counter.weight.loss.diet.nutrition.macro.tracker.fasting"
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label="Get Calo on Google Play"
                                    >
                                        <img
                                            src="/static/image/homepage/google-play-en.png"
                                            alt="Get it on Google Play"
                                        />
                                    </a>
                                </div>
                                <div className="google-app-download-meta" aria-hidden="true">
                                    <span>iPhone and iPad</span>
                                    <span>Android devices</span>
                                </div>
                            </aside>
                        </div>
                    </div>
                </section>

                <section className="google-app-section">
                    <div className="google-app-section-heading">
                        <p className="google-app-eyebrow">What Calo does</p>
                        <h2>Core app functionality</h2>
                    </div>
                    <div className="google-app-grid google-app-grid-three">
                        {featureCards.map(card => (
                            <article className="google-app-card" key={card.title}>
                                <h3>{card.title}</h3>
                                <p>{card.body}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="google-app-section">
                    <div className="google-app-section-heading">
                        <p className="google-app-eyebrow">Transparency</p>
                        <h2>How Calo uses requested data</h2>
                    </div>
                    <div className="google-app-grid google-app-grid-three">
                        {dataCards.map(card => (
                            <article className="google-app-card" key={card.title}>
                                <h3>{card.title}</h3>
                                <ul>
                                    {card.items.map(item => (
                                        <li key={item}>{item}</li>
                                    ))}
                                </ul>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="google-app-section google-app-highlight">
                    <div className="google-app-highlight-copy">
                        <p className="google-app-eyebrow">Need help?</p>
                        <h2>Support and legal information</h2>
                        <p>
                            If users or reviewers need help with Calo, account access, or health-data
                            connections, they can contact the Calo support team directly.
                        </p>
                    </div>
                    <div className="google-app-highlight-actions">
                        <a className="google-app-primary" href="mailto:website@caloapp.ai">website@caloapp.ai</a>
                        <a className="google-app-text-link" href="/terms-of-use">View Terms of Use</a>
                    </div>
                </section>
            </main>

            <footer className="google-app-footer">
                <p>Next Vision Limited operates the Calo mobile application.</p>
                <div className="google-app-footer-links">
                    <a href="/app">App Homepage</a>
                    <a href="/privacy-policy">Privacy Policy</a>
                    <a href="/terms-of-use">Terms of Use</a>
                </div>
            </footer>
        </div>
    );
};
